/**
 * React Query hook for fetching transactions excluded due to pricing errors
 *
 * Returns transactions that have error_type = "error-price" from the error_log table
 * These are transactions that were excluded from tax calculations due to missing or failed price lookups
 */

import { useQuery } from "@tanstack/react-query";
import { authorizedAxios } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";

export interface PricingErrorTransaction {
  id: number;
  transaction_id: string;
  error_type: string;
  error_category: string;
  error_message: string;
  error_details: any;
  priority: 'must_fix' | 'should_fix' | 'informational';
  affected_asset: string;
  affected_exchange: string;
  is_dismissed: boolean;
  detected_at: string;
  created_at: string;
  // Transaction details from joined data
  txn_data?: {
    tradeid: string;
    datetime: string;
    type: string;
    buyasset: string;
    sellasset: string;
    buyamount: number;
    sellamount: number;
    exchange: string;
  };
}

export interface PricingErrorsResponse {
  success: boolean;
  data: {
    errors: PricingErrorTransaction[];
    summary: {
      total: number;
      by_asset: Record<string, number>;
      by_exchange: Record<string, number>;
    };
  };
}

/**
 * Hook to fetch all pricing errors for the current user
 */
export const usePricingErrors = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<PricingErrorsResponse>({
    queryKey: ["pricing-errors"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();

      // Fetch both types of pricing errors
      // We need to make two separate API calls since the endpoint filters by single error type
      const [errorPriceResponse, errorPriceNoCoinpaprikaResponse] = await Promise.all([
        authorizedAxios(token).get("/audit/error-price", {
          params: { limit: 1000, cursor: 0 }
        }),
        authorizedAxios(token).get("/audit/error-price-no-coinpaprika-id", {
          params: { limit: 1000, cursor: 0 }
        }).catch(() => ({ data: { items: [] } })) // Fallback for new error type
      ]);

      // Combine both result sets
      const response = {
        data: {
          items: [
            ...(errorPriceResponse.data.items || []),
            ...(errorPriceNoCoinpaprikaResponse.data.items || [])
          ]
        }
      };

      // Transform the paginated response to our expected format
      const items = response.data.items || [];

      // Calculate summary statistics
      const byAsset: Record<string, number> = {};
      const byExchange: Record<string, number> = {};

      // Transform items to include parsed transaction data
      const transformedItems = items.map((item: any) => {
        // Parse error_data if it's a string, otherwise use as-is
        const txnData = typeof item.error_data === 'string'
          ? JSON.parse(item.error_data)
          : item.error_data;

        // Count by asset
        const asset = txnData?.o_buyasset || txnData?.buyasset || txnData?.o_sellasset || txnData?.sellasset || 'Unknown';
        byAsset[asset] = (byAsset[asset] || 0) + 1;

        // Count by exchange
        const exchange = txnData?.exchange || 'Unknown';
        byExchange[exchange] = (byExchange[exchange] || 0) + 1;

        return {
          ...item,
          affected_asset: asset,
          affected_exchange: exchange,
          txn_data: {
            tradeid: txnData?.tradeid || txnData?.ext_txn_id,
            datetime: txnData?.datetime ? new Date(txnData.datetime).toISOString() : null,
            type: txnData?.type,
            buyasset: txnData?.o_buyasset || txnData?.buyasset,
            sellasset: txnData?.o_sellasset || txnData?.sellasset,
            buyamount: txnData?.buyqty,
            sellamount: txnData?.sellqty,
            exchange: txnData?.exchange
          }
        };
      });

      return {
        success: true,
        data: {
          errors: transformedItems,
          summary: {
            total: transformedItems.length,
            by_asset: byAsset,
            by_exchange: byExchange
          }
        }
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch summary count of pricing errors
 * Lighter weight version that only fetches the count from audit summary
 */
export const usePricingErrorCount = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<number>({
    queryKey: ["pricing-errors", "count"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get("/audit/summary");

      // Get count from byErrorType - sum both error types
      const byErrorType = response.data?.data?.byErrorType || {};
      const count = (byErrorType["error-price"] || 0) + (byErrorType["error-price-no-coinpaprika-id"] || 0);
      return count;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 2,
  });
};
